import styled from '@emotion/styled/macro';
import vars from '@variables';
import { keyframes } from '@emotion/react';

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.4;
  }
  70% {
    transform: scale(2.2);
    opacity: 0;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
`;

export const BackgroundColor = styled.div<{
  backgroundColor?: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  width: 100vw;
  height: 100%;
  margin-left: calc(-50vw + 50%);
  background-color: ${({ backgroundColor }) => backgroundColor};
  overflow: hidden;

  & > div {
    height: 100% !important;
    width: 100% !important;
  }

  img {
    object-fit: cover !important;
    object-position: center !important;
    width: 100% !important;
    height: 100% !important;
  }
`;

export const MapContainer = styled.div<{
  isFullWidth?: boolean;
}>`
  position: relative;
  width: ${({ isFullWidth }) => (isFullWidth ? '100vw' : '100%')};
  margin: ${({ isFullWidth }) => (isFullWidth ? `0 calc(-50vw + 50%)` : '0')};

  &::before,
  &::after {
    content: '';
    display: ${({ isFullWidth }) => (isFullWidth ? 'none' : 'block')};
    height: ${vars.spacingXl};
  }

  @media (max-width: 768px) {
    height: calc(70vh - ${vars.mobileNavBarHeight});
    margin: ${({ isFullWidth }) => (isFullWidth ? '0 calc(-50vw + 50%)' : '0')};
    overflow: hidden;
    border: 1px solid ${vars.transparentBorderGrey};

    &::before,
    &::after {
      display: none;
    }
  }
`;

export const ImageWrapper = styled.div<{
  isMobile?: boolean;
  isFullWidth?: boolean;
}>`
  position: relative;
  width: 100%;
  height: ${({ isMobile }) => (isMobile ? '100%' : 'auto')};

  @media (min-width: 768px) {
    img {
      width: 100%;
      height: auto;
      display: block;
      border-radius: ${({ isFullWidth }) => (isFullWidth ? '0' : '12px')};
    }

    .gatsby-image-wrapper {
      width: 100%;
      height: auto;
    }
  }
`;

export const Pin = styled.button<{ color?: string; isActive?: boolean }>`
  position: absolute;
  width: 2.4rem;
  height: 2.4rem;
  padding: 0;
  border: ${({ isActive }) => (isActive ? 'none' : `1px solid ${vars.black}`)};
  background-color: ${props => props.color};
  color: ${props => props.color};
  border-radius: 50%;
  cursor: pointer;
  transform: translate(-50%, -50%);
  transform-origin: center;
  transition: all ${vars.defaultTransitionDuration} ease;
  .transform-component-module_content__FBWxo & {
    transform: translate(-50%, -50%) scale(calc(1 / var(--scale, 1)));
  }

  @media (max-width: 768px) {
    width: 1.8rem;
    height: 1.8rem;
  }

  // outer ring
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    background-color: ${props => props.color};
    border-radius: 50%;
    opacity: ${props => (props.isActive ? 0.4 : 0)};
    transition: opacity ${vars.defaultTransitionDuration} ease;
    animation: ${pulse} 2s ease-out infinite;
    z-index: -1;
  }

  &:hover {
    background-color: ${props => props.color};
    border: none;

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translate(-50%, -50%) scale(0.9);
  }
`;

export const Drawer = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(${vars.spacingXl} + 2.4rem);
  right: 2.4rem;
  height: calc(100% - ${vars.spacingXl} * 2 - 4.8rem);
  width: 60%;

  @media screen and (min-width: 1440px) {
    width: 40%;
  }

  background: ${vars.white};
  transform: ${props =>
    props.isOpen ? 'translate(0, 0)' : 'translate(100%, 0)'};
  opacity: ${props => (props.isOpen ? '1' : '0')};
  transition:
    transform ${vars.defaultTransitionDuration} cubic-bezier(0.4, 0, 0.2, 1),
    opacity ${vars.defaultTransitionDuration} cubic-bezier(0.4, 0, 0.2, 1),
    visibility 0s
      ${props => (props.isOpen ? '0s' : vars.defaultTransitionDuration)};
  visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
  z-index: 10;
  box-shadow: -8px 8px 12px #00000040;
  border-radius: 8px;
  display: flex;
  padding: 32px;
  flex-direction: column;
  padding-top: 40px;

  @media (max-width: 768px) {
    height: 100dvh;
    max-height: 100dvh;
    width: 100%;
    position: fixed;
    border-radius: 0px;
    top: auto;
    bottom: 0;
    right: 0;
    left: 0;
    margin: 0;
    z-index: 10000;
    transform: ${props =>
      props.isOpen ? 'translateY(0)' : 'translateY(100%)'};
    transition:
      transform ${vars.defaultTransitionDuration} cubic-bezier(0.4, 0, 0.2, 1),
      opacity ${vars.defaultTransitionDuration} cubic-bezier(0.4, 0, 0.2, 1),
      visibility 0s
        ${props => (props.isOpen ? '0s' : vars.defaultTransitionDuration)};
    overflow-y: auto;
  }
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const CloseSection = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  gap: ${vars.spacingS};

  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    padding: ${vars.spacingM};
    background: ${vars.coreDarkGrey};
    justify-content: center;
    color: ${vars.white};
  }
`;

export const CloseText = styled.span`
  ${vars.fontSize2}
  color: ${vars.coreDarkGrey};

  @media (max-width: 768px) {
    color: ${vars.white};
  }
`;

export const CloseButton = styled.span<{ isMobile: boolean }>`
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  background-color: ${vars.coreDarkGrey};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 2.4rem;
    height: 2.4rem;
    background: none;
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 2px;
    height: ${({ isMobile }) => (isMobile ? '1.6rem' : '1.2rem')};
    background: ${vars.white};
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`;

export const DrawerContent = styled.div`
  max-height: 100%;
  overflow-y: auto;
  flex: 1;
  ${vars.fontSize2}

  p {
    margin-bottom: ${vars.spacingM};
    line-height: 1.6;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: ${vars.spacingL} 0 ${vars.spacingS};
    color: ${vars.coreDarkGrey};
  }

  h1 {
    ${vars.fontSize8}
  }
  h2 {
    ${vars.fontSize7}
  }
  h3 {
    ${vars.fontSize6}
  }
  h4 {
    ${vars.fontSize5}
  }

  ul,
  ol {
    margin: ${vars.spacingM} 0;
    padding-left: ${vars.spacingL};

    li {
      margin-bottom: ${vars.spacingXs};

      &::marker {
        color: ${vars.coreDarkGrey};
      }
    }
  }

  blockquote {
    margin: ${vars.spacingS} 0 ${vars.spacingM} 0;
    position: relative;

    &::before {
      content: '\\201C';
      ${vars.fontSize10}
      position: absolute;
    }

    p {
      padding: 0 0 ${vars.spacingS} ${vars.spacingXl};
      font-style: italic;
      &:first-of-type {
        padding-top: 1rem;
      }
    }
  }

  a {
    color: ${vars.coreDarkGrey};
    text-decoration: none;
    border-bottom: 1px solid currentColor;
    transition: all ${vars.defaultTransitionDuration} ease;
  }

  code {
    background: ${vars.greyHavens};
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }

  pre {
    background: ${vars.coreDarkGrey};
    color: ${vars.white};
    padding: ${vars.spacingM};
    border-radius: 4px;
    overflow-x: auto;
    margin: ${vars.spacingM} 0;

    code {
      background: none;
      padding: 0;
      color: inherit;
    }
  }

  hr {
    border: none;
    border-top: 1px solid ${vars.greyHavens};
    margin: ${vars.spacingL} 0;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: ${vars.spacingM} 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: ${vars.spacingM} 0;

    th,
    td {
      padding: ${vars.spacingS};
      border: 1px solid ${vars.greyHavens};
      text-align: left;
    }

    th {
      background: ${vars.greyHavens};
      font-weight: bold;
    }
  }

  @media (max-width: 768px) {
    padding-bottom: calc(${vars.spacingM} * 2 + 3.2rem);
  }
`;

export const DrawerTitle = styled.h3<{
  color?: string;
  useDarkPillText?: boolean;
}>`
  color: ${props =>
    props.useDarkPillText || props.color === '#ffffff'
      ? vars.coreDarkGrey
      : props.color};
  ${vars.fontSize8}
  margin-bottom: 8px;
`;

export const PillTag = styled.span<{
  color?: string;
  useDarkPillText?: boolean;
}>`
  display: inline-block;
  padding: ${vars.spacingXs} ${vars.spacingS};
  background-color: ${props =>
    props.useDarkPillText || props.color === '#ffffff'
      ? vars.coreDarkGrey
      : props.color};
  color: ${vars.white};
  border-radius: 200px;
  margin-bottom: ${vars.spacingM};
  ${vars.fontSize2}
`;

export const NavigationHint = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 12px 20px;
  border-radius: 20px;
  font-size: 14px;
  z-index: 10;
  pointer-events: none;
  transition: opacity 0.3s ease-in;
  opacity: 1;
`;
